import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import './ApiKeys.css';

const formatDate = (val) => {
  if (val == null) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const formatValue = (value) => {
  if (value == null) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const PAGE_SIZES = [10, 25, 50, 100];

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
    pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const ApiKeys = () => {
  const { apiCall } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keys, setKeys] = useState([]);
  const [keyIdFilter, setKeyIdFilter] = useState('');
  const [debouncedKeyIdFilter, setDebouncedKeyIdFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState(''); // '' | 'true' | 'false'
  const [selectedKey, setSelectedKey] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Usage modal state
  const [usageKey, setUsageKey] = useState(null);
  const [usageDocs, setUsageDocs] = useState([]);
  const [usageDays, setUsageDays] = useState(30);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [usagePage, setUsagePage] = useState(1);
  const [usagePageSize, setUsagePageSize] = useState(10);

  // Usage entry detail modal
  const [selectedUsageEntry, setSelectedUsageEntry] = useState(null);

  // 204-safe wrapper
  const apiCallNoBody = async (endpoint, options) => {
    try { return await apiCall(endpoint, options); }
    catch (err) { if (err instanceof SyntaxError) return null; throw err; }
  };

  const fetchKeys = async (page = currentPage, size = pageSize, q = debouncedKeyIdFilter, active = isActiveFilter) => {
    setLoading(true);
    setError('');
    try {
      let url = `/admin/api-keys?page=${page}&page_size=${size}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      if (active) url += `&is_active=${active}`;
      const data = await apiCall(url);
      setKeys(data.api_keys || data.keys || []);
      const pg = data.pagination || data;
      setTotal(pg.total || 0);
      setTotalPages(pg.total_pages || 1);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Debounce key ID filter
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedKeyIdFilter(keyIdFilter); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [keyIdFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchKeys(currentPage, pageSize, debouncedKeyIdFilter, isActiveFilter); }, [currentPage, pageSize, debouncedKeyIdFilter, isActiveFilter]);

  const handleDelete = async (keyId) => {
    setDeleteLoading(true);
    setError('');
    try {
      await apiCallNoBody(`/admin/api-keys/${keyId}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      await fetchKeys(currentPage, pageSize, debouncedKeyIdFilter, isActiveFilter);
    } catch (err) {
      setError(err.message);
    }
    setDeleteLoading(false);
  };

  const handleViewUsage = async (key, days = usageDays) => {
    setUsageKey(key);
    setUsageDocs([]);
    setUsageError('');
    setUsagePage(1);
    setUsageLoading(true);
    try {
      const data = await apiCall(`/admin/api-keys/${key.id || key.key_id}/usage?days=${days}`);
      const docs = data.usage || data.daily || data.items || [];
      // Sort newest first by date field (try common names)
      docs.sort((a, b) => {
        const aDate = a.date || a.day || a._id || '';
        const bDate = b.date || b.day || b._id || '';
        return bDate.localeCompare(aDate);
      });
      setUsageDocs(docs);
    } catch (err) {
      setUsageError(err.message);
    }
    setUsageLoading(false);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator inactive">↕</span>;
    return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Client-side sort on current page
  const displayed = [...keys].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const usageTotalPages = Math.max(1, Math.ceil(usageDocs.length / usagePageSize));
  const usagePaginated = usageDocs.slice((usagePage - 1) * usagePageSize, usagePage * usagePageSize);

  if (loading && keys.length === 0) return <Loader />;

  return (
    <div className="ak-page">
      <div className="page-header">
        <h2 className="page-title">API Keys ({total})</h2>
        <button onClick={() => fetchKeys(currentPage, pageSize, debouncedKeyIdFilter, isActiveFilter)} className="btn-icon" disabled={loading}>
          <RefreshIcon className={loading ? 'spinning' : ''} sx={{ fontSize: '1.25rem' }} />
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="vt-filters">
        <div className="vt-filter-field">
          <label className="vt-filter-label">Search</label>
          <div className="search-input-wrapper">
            <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
            <input
              type="text"
              placeholder="Search by key ID, name, email, or user..."
              value={keyIdFilter}
              onChange={(e) => setKeyIdFilter(e.target.value)}
              className="search-input"
            />
            {keyIdFilter && (
              <button onClick={() => setKeyIdFilter('')} className="clear-search-btn">
                <CloseIcon sx={{ fontSize: '1rem' }} />
              </button>
            )}
          </div>
        </div>

        <div className="vt-filter-field" style={{ flex: '0 0 10rem' }}>
          <label className="vt-filter-label">Status</label>
          <select
            value={isActiveFilter}
            onChange={(e) => { setIsActiveFilter(e.target.value); setCurrentPage(1); }}
            className="detail-select"
            style={{ width: '100%', padding: '0.625rem 0.75rem', fontSize: '0.9375rem' }}
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      <div className="table-card">
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort('id')}>
                  Key ID <SortIndicator field="id" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('user_email')}>
                  Email <SortIndicator field="user_email" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('user_display_name')}>
                  User Name <SortIndicator field="user_display_name" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('user_id')}>
                  User ID <SortIndicator field="user_id" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('name')}>
                  Key Name <SortIndicator field="name" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('is_active')}>
                  Active <SortIndicator field="is_active" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('created_at')}>
                  Created At <SortIndicator field="created_at" />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-results">No API keys found</td>
                </tr>
              ) : (
                displayed.map((key) => {
                  const keyId = key.id || key.key_id;
                  return (
                    <tr key={keyId} className="user-row">
                      <td className="td-id" title={keyId}>{keyId}</td>
                      <td>{key.user_email || '—'}</td>
                      <td>{key.user_display_name || '—'}</td>
                      <td className="td-id" title={key.user_id}>{key.user_id || '—'}</td>
                      <td>{key.name || key.label || '—'}</td>
                      <td>
                        <span className={`badge ${key.is_active === false ? 'badge-gray' : 'badge-orange'}`}>
                          {key.is_active === false ? 'inactive' : 'active'}
                        </span>
                      </td>
                      <td className="td-date">{formatDate(key.created_at)}</td>
                      <td className="td-actions">
                        <button
                          className="btn-icon-sm"
                          title="View details"
                          onClick={() => setSelectedKey(key)}
                        >
                          <VisibilityIcon sx={{ fontSize: '1.125rem' }} />
                        </button>
                        <button
                          className="btn-icon-sm btn-usage"
                          title="View usage"
                          onClick={() => handleViewUsage(key)}
                        >
                          <BarChartIcon sx={{ fontSize: '1.125rem' }} />
                        </button>
                        <button
                          className="btn-icon-sm btn-delete"
                          title="Delete key"
                          onClick={() => setDeleteConfirm(keyId)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: '1.125rem' }} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="rows-per-page">
            <span>Rows per page:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <span className="pagination-info">
            {total === 0
              ? 'No results'
              : `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, total)} of ${total}`}
          </span>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn page-nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
              </button>
              {getPageNumbers(currentPage, totalPages).map((p) =>
                typeof p === 'string' ? (
                  <span key={p} className="page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`page-btn${p === currentPage ? ' active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="page-btn page-nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Key Detail Modal */}
      {selectedKey && (
        <div className="modal-overlay" onClick={() => setSelectedKey(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">API Key Details</h3>
              <button onClick={() => setSelectedKey(null)} className="btn-close">
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              {Object.entries(selectedKey).map(([k, v]) => (
                <div className="detail-row" key={k}>
                  <span className="detail-label">{k}:</span>
                  <span className="detail-value detail-value-sm">{formatValue(v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete API Key</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Delete API key <strong className="user-id-highlight">{deleteConfirm}</strong>?
              </p>
              <p className="confirm-subtext">This action cannot be undone.</p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {usageKey && (
        <div className="modal-overlay" onClick={() => !usageLoading && setUsageKey(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Usage — Daily</h3>
                <p className="ak-modal-subtitle">{usageKey.id || usageKey.key_id}</p>
              </div>
              <button onClick={() => setUsageKey(null)} className="btn-close" disabled={usageLoading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body ak-modal-body-table">
              <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label className="vt-filter-label" style={{ margin: 0 }}>Range:</label>
                <select
                  value={usageDays}
                  onChange={(e) => {
                    const d = Number(e.target.value);
                    setUsageDays(d);
                    handleViewUsage(usageKey, d);
                  }}
                  className="detail-select"
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  disabled={usageLoading}
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>

              {usageLoading && <p className="ak-usage-state">Loading usage data…</p>}
              {!usageLoading && usageError && <p className="ak-usage-error">{usageError}</p>}
              {!usageLoading && !usageError && usageDocs.length === 0 && (
                <p className="ak-usage-state">No usage data found.</p>
              )}
              {!usageLoading && !usageError && usageDocs.length > 0 && (
                <>
                  <div className="users-table-wrapper">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usagePaginated.map((entry, idx) => {
                          const date = entry.date || entry.day || entry._id || `row-${idx}`;
                          return (
                            <tr key={date} className="user-row">
                              <td className="ak-date-cell">{date}</td>
                              <td className="td-actions">
                                <button
                                  className="btn-icon-sm"
                                  title="View details"
                                  onClick={() => setSelectedUsageEntry({ ...entry, _id: date })}
                                >
                                  <VisibilityIcon sx={{ fontSize: '1.125rem' }} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-footer">
                    <div className="rows-per-page">
                      <span>Rows per page:</span>
                      <select value={usagePageSize} onChange={(e) => { setUsagePageSize(Number(e.target.value)); setUsagePage(1); }}>
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <span className="pagination-info">
                      {`${(usagePage - 1) * usagePageSize + 1}–${Math.min(usagePage * usagePageSize, usageDocs.length)} of ${usageDocs.length}`}
                    </span>
                    {usageTotalPages > 1 && (
                      <div className="pagination">
                        <button
                          className="page-btn page-nav"
                          onClick={() => setUsagePage((p) => Math.max(1, p - 1))}
                          disabled={usagePage === 1}
                          title="Previous page"
                        >
                          <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
                        </button>
                        {getPageNumbers(usagePage, usageTotalPages).map((p) =>
                          typeof p === 'string' ? (
                            <span key={p} className="page-ellipsis">…</span>
                          ) : (
                            <button
                              key={p}
                              className={`page-btn${p === usagePage ? ' active' : ''}`}
                              onClick={() => setUsagePage(p)}
                            >
                              {p}
                            </button>
                          )
                        )}
                        <button
                          className="page-btn page-nav"
                          onClick={() => setUsagePage((p) => Math.min(usageTotalPages, p + 1))}
                          disabled={usagePage === usageTotalPages}
                          title="Next page"
                        >
                          <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage Entry Detail Modal */}
      {selectedUsageEntry && (
        <div className="modal-overlay" onClick={() => setSelectedUsageEntry(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Usage Details</h3>
                <p className="ak-modal-subtitle">{selectedUsageEntry._id}</p>
              </div>
              <button onClick={() => setSelectedUsageEntry(null)} className="btn-close">
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              {Object.entries(selectedUsageEntry)
                .filter(([k]) => k !== '_id')
                .map(([k, v]) => (
                  <div className="detail-row" key={k}>
                    <span className="detail-label">{k}:</span>
                    <span className="detail-value detail-value-sm">{formatValue(v)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
